import { Add } from '@mui/icons-material'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import { isDefined } from 'utils/type-guard/isDefined'
import { globalIntegrationsMetadata } from '@omnea-app/config/globalIntegrations'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useListExtOpsIntegrations } from '@omnea-app/fe-settings/integrations/useListExtOpsIntegrations'
import { useListOrganisationIntegrations } from '@omnea-app/fe-settings/integrations/useListOrganisationIntegrations'
import { CustomHttpIntegrationIcon } from '../settings/CustomHttpIntegrationIcon'
import { SettingsMenuItem } from '../settings/SettingsSidebarContent'

const publicAPIDocsLink = process.env.NEXT_PUBLIC_API_README_URL

export const useIntegrationsItems = (hasPermissions: boolean): SettingsMenuItem[] => {
  const { t } = useTranslation('common', { keyPrefix: 'sidebar' })
  const { data: enabledIntegrations } = useListOrganisationIntegrations({ enabled: hasPermissions })
  const { customIntegrations } = useListExtOpsIntegrations()
  const { PUBLIC_API: isPublicApiEnabled, PUBLIC_API_UI: isPublicApiUiEnabled } = useFeatureFlags()

  if (!hasPermissions) {
    return []
  }

  const mappedIntegrations =
    enabledIntegrations?.map(({ id, integration }) => ({
      id,
      ...globalIntegrationsMetadata[integration],
    })) ?? []

  const enabledIntegrationsData = mappedIntegrations
    // TODO(SamC): ExtOps integrations when their settings page is built
    .filter((integration) => integration.integrationSource === 'Accounting' || integration.integrationSource === 'Native')
    // We handle the public api integration separately below
    .filter((integration) => integration.integration !== 'public-api')
    // Require admin with config permissions to see accounting integrations in sidebar
    .map((integration) => ({
      ...integration,
      ...(integration.integrationSource === 'Accounting' && {
        requiredPermission: {
          isAdmin: true,
          hasConfigPermissions: true,
        },
      }),
    }))

  // We only want to show it in the sidebar if the feature flag is enabled, but the UI feature flag is not (because then it lives under the API settings)
  const shouldShowPublicApiIntegration = isPublicApiEnabled && !isPublicApiUiEnabled && isDefined(publicAPIDocsLink)

  // An org can have multiple public api integrations, but we only want to show one in the sidebar
  const enabledPublicApiIntegration = mappedIntegrations.find((integration) => integration.integration === 'public-api')
  const publicApiIntegration: SettingsMenuItem[] =
    shouldShowPublicApiIntegration && enabledPublicApiIntegration
      ? [
          {
            label: enabledPublicApiIntegration.title,
            icon: <Image src={enabledPublicApiIntegration.logo} alt={enabledPublicApiIntegration.title} width={16} height={16} />,
            href: publicAPIDocsLink,
            newTab: true,
          },
        ]
      : []

  const customIntegrationsData = customIntegrations.filter(
    (customIntegration) =>
      customIntegration.enabled &&
      // Custom HTTP accounting integrations
      customIntegration.type === 'http' &&
      customIntegration.isAccountingIntegration
  )

  return [
    ...enabledIntegrationsData.map((integration) => ({
      label: integration.title,
      icon: <Image src={integration.logo} alt={integration.title} width={16} height={16} />,
      href: `/settings/integrations/${integration.id}`,
      requiredPermission: integration.requiredPermission,
    })),
    ...publicApiIntegration,
    ...customIntegrationsData.map((customIntegration) => ({
      label: customIntegration.name,
      icon: <CustomHttpIntegrationIcon customIntegration={customIntegration} />,
      href: `/settings/integrations/${customIntegration.customIntegrationId}`,
    })),
    {
      label: t('integrations-explore-more'),
      icon: <Add sx={{ width: 16, height: 16 }} />,
      href: '/settings/integrations',
    },
  ]
}
